import React, { useContext } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  LEARNER_AFTER_GROUP_SESSION_FEEDBACK_FORM,
  LEARNER_RECORDING,
  LEARNER_SESSION,
} from "utility/graphQl/query";

import appInfo from "utility/app-info";
import {
  LearnerAfterGroupSessionFeedbackForm,
  LearnerGroupSession,
  LearnerRecording,
} from "apps/user/learner/api/types";

import { LEARNER_SUBMIT_AFTER_GROUP_SESSION_FEEDBACK } from "utility/graphQl/mutation";
import { logError } from "utility/Function";
import { useNavigate } from "react-router-dom";
import { usePatternBackground } from "./pattern-background";
import { trackEvent } from "utility/mixpanel";
import getFeedbackSteps from "apps/user/learner/pages/feedback/feedback-steps";
import { useTranslation } from "lib/i18n/use-translation";
import { sentenceCase } from "utility/Function";
import { FeedbackStep } from "apps/user/learner/pages/feedback/types";

interface SessionFeedbackContextInterface {
  sessionDetailLoading: boolean;
  sessionDetailError: Error | null;
  sessionDetailData: any;
  postSessionFeedbackLoading: boolean;
  postSessionFeedbackError: Error | null;
  postSessionFeedbackData: any;
  isPostSessionFeedbackSubmissionError: boolean;
  submitLoading: boolean;
  imageUrl: string;
  practitionerName: string;
  title: string;
  sessionCount: string;
  steps: FeedbackStep[];
  step: FeedbackStep;
  type: "LIVE" | "RECORDING";
  stepsFraction: string;
  groupSessionId: string;
  setIsPostSessionFeedbackSubmissionError: (value: boolean) => void;
  onSelectButtonOption: (id: string, value: string) => void;
  onChangeTextInput: (value: string) => void;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
}

const SessionFeedbackContext =
  React.createContext<SessionFeedbackContextInterface>({
    sessionDetailLoading: false,
    sessionDetailError: null,
    sessionDetailData: null,
    postSessionFeedbackLoading: false,
    postSessionFeedbackError: null,
    postSessionFeedbackData: null,
    submitLoading: false,
    isPostSessionFeedbackSubmissionError: false,
    imageUrl: "",
    practitionerName: "",
    title: "",
    sessionCount: "0",
    type: "RECORDING",
    steps: [],
    step: {} as FeedbackStep,
    stepsFraction: "1/4",
    groupSessionId: "",
    setIsPostSessionFeedbackSubmissionError: () => null,
    onSelectButtonOption: () => null,
    onChangeTextInput: () => null,
    onNext: () => null,
    onBack: () => null,
    onSubmit: () => null,
  });

interface SessionFeedbackProviderProps {
  sessionId: string;
  type: "LIVE" | "RECORDING";
  children: React.ReactNode;
}
const SessionFeedbackProvider: React.FC<SessionFeedbackProviderProps> = ({
  sessionId,
  type,
  children,
}) => {
  const [
    isPostSessionFeedbackSubmissionError,
    setIsPostSessionFeedbackSubmissionError,
  ] = React.useState(false);
  const { t } = useTranslation("feedback");
  const [feedbackSteps, setFeedbackSteps] = React.useState<FeedbackStep[]>(() =>
    getFeedbackSteps(t)
  );
  const [currentStep, setCurrentStep] = React.useState(0);
  const step = feedbackSteps[currentStep];
  const stepsFraction = `${currentStep + 1}/${feedbackSteps.length}`;
  const navigate = useNavigate();
  const { removePatternOverlay } = usePatternBackground();

  let sessionDetailQuery;
  if (type === "RECORDING") {
    sessionDetailQuery = LEARNER_RECORDING;
  } else {
    sessionDetailQuery = LEARNER_SESSION;
  }

  const {
    loading: sessionDetailLoading,
    error: sessionDetailError,
    data: sessionDetailData,
  } = useQuery(sessionDetailQuery, {
    fetchPolicy: "no-cache",
    onError(error) {
      logError("Error while fetching session detail", error);
    },
    variables: {
      appInfo,
      id: sessionId,
    },
  });

  const {
    loading: postSessionFeedbackLoading,
    error: postSessionFeedbackError,
    data: postSessionFeedbackData,
  } = useQuery(LEARNER_AFTER_GROUP_SESSION_FEEDBACK_FORM, {
    fetchPolicy: "no-cache",
    onError(error) {
      logError("Error while fetching session feedback form page", error);
    },
    variables: {
      appInfo,
      groupSessionId: sessionId,
    },
  });

  const [submitAfterGroupSessionFeedback, { loading: submitLoading }] =
    useMutation(LEARNER_SUBMIT_AFTER_GROUP_SESSION_FEEDBACK);

  const sessionData =
    type === "RECORDING"
      ? (sessionDetailData?.learnerRecording as LearnerRecording)
      : (sessionDetailData?.learnerSession as LearnerGroupSession);

  const imageUrl =
    type === "RECORDING"
      ? (sessionData as LearnerRecording)?.imageUrl
      : (sessionData as LearnerGroupSession)?.imageUrl;
  const fullName = sessionData?.practitioner?.fullName;

  const practitionerName = fullName
    ? sentenceCase(fullName)
    : "Practitioner Name";

  const title = sessionData?.title || "Session Title";
  const afterGroupSessionForm =
    postSessionFeedbackData?.learnerAfterGroupSessionFeedbackForm as LearnerAfterGroupSessionFeedbackForm;
  const sessionCount = afterGroupSessionForm?.sessionsAttendedThisMonth?.value;

  const handleSelectButtonOption = (responseId: string, value: string) => {
    step.response = {
      id: responseId,
      value: value,
    };
    const updatedSteps = feedbackSteps.map((s) => {
      if (s.id === step.id) {
        return {
          ...s,
          response: {
            id: responseId,
            value: value,
          },
        };
      }
      return s;
    });
    setFeedbackSteps(updatedSteps);
    handleNext();
  };
  const handleChangeTextInput = (value: string) => {
    step.response = {
      id: step.id,
      value: value,
    };
    const updatedSteps = feedbackSteps.map((s) => {
      if (s.id === step.id) {
        return {
          ...s,
          response: {
            id: step.id,
            value: value,
          },
        };
      }
      return s;
    });
    setFeedbackSteps(updatedSteps);
  };
  const handleNext = () => {
    if (currentStep < feedbackSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSubmit = () => {
    const inputArray = feedbackSteps.map(({ id, response }) => ({
      id: id,
      value: String(response?.value),
    }));

    submitAfterGroupSessionFeedback({
      variables: {
        appInfo,
        groupSessionId: sessionId,
        type: type,
        input: inputArray,
      },
    })
      .then((res) => {
        if (res.data?.learnerSubmitAfterGroupSessionFeedback.ok) {
          if (type === "LIVE") {
            trackEvent("Live Session Feedback Submit Clicked", {
              "Session ID": sessionId,
              "Session Title": title,
              Response: inputArray,
            });
          } else {
            trackEvent("Recording Feedback Submit Clicked", {
              "Recording ID": sessionId,
              "Recording Title": title,
              Response: inputArray,
            });
          }

          if (isPostSessionFeedbackSubmissionError) {
            setIsPostSessionFeedbackSubmissionError(false);
          }
          removePatternOverlay(false);
          navigate("/user/home", {
            state: { showFeedbackSubmittedToast: true },
          });
        }
      })
      .catch((error) => {
        logError("Error while submitting session feedback", error);
        setIsPostSessionFeedbackSubmissionError(true);
      });
  };

  return (
    <SessionFeedbackContext.Provider
      value={{
        sessionDetailLoading,
        sessionDetailError,
        sessionDetailData,
        postSessionFeedbackLoading,
        postSessionFeedbackError,
        postSessionFeedbackData,

        isPostSessionFeedbackSubmissionError,
        submitLoading,
        imageUrl,
        practitionerName,
        title,
        sessionCount,
        steps: feedbackSteps,
        step,
        type,
        stepsFraction,
        groupSessionId: sessionId,
        setIsPostSessionFeedbackSubmissionError,
        onSelectButtonOption: handleSelectButtonOption,
        onChangeTextInput: handleChangeTextInput,
        onNext: handleNext,
        onBack: handleBack,
        onSubmit: handleSubmit,
      }}
    >
      {children}
    </SessionFeedbackContext.Provider>
  );
};

export const useSessionFeedback = () => useContext(SessionFeedbackContext);
export default SessionFeedbackProvider;
